import { ReactNode, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';
import { useResource } from 'react-resource-router';

import {
  FeatureProvider as BaseProvider,
  EnvironmentType,
  getUserIdentifier,
  FeatureFlagUser,
} from '@atlassian/bitbucket-features';
import { uncurlyUuid } from '@atlassian/bitkit-analytics';

import { useAppData } from 'src/app/data';
import { FeatureKeys } from 'src/config/feature-flag-keys';
import { workspaceNavigationResource } from 'src/sections/workspace/workspace-navigation-resource';
import { getCurrentProjectUuid } from 'src/selectors/project-selectors';
import {
  getCurrentRepositoryProjectUuid,
  getCurrentRepositoryUuid,
  getCurrentRepositoryWorkspaceUUID,
} from 'src/selectors/repository-selectors';
import { getCurrentWorkspaceUUID } from 'src/selectors/workspace-selectors';
import { analyticsClient } from 'src/utils/analytics/client';
import { BbEnv, getBbEnv } from 'src/utils/bb-env';

export const envMap: { [bbEnv in BbEnv]: EnvironmentType } = {
  [BbEnv.Development]: EnvironmentType.DEV,
  [BbEnv.Test]: EnvironmentType.PROD, // Test is prod-like for application changes
  [BbEnv.Staging]: EnvironmentType.STAGING,
  [BbEnv.Integration]: EnvironmentType.STAGING,
  [BbEnv.Production]: EnvironmentType.PROD,
};

const uncurl = (uuid?: string | null) =>
  uncurlyUuid(uuid || '') || uuid || undefined;

export const FeatureProvider = ({ children }: { children: ReactNode }) => {
  // For now we need to avoid unmounting the child components due to a
  // bug with the AK router. This can be replaced with `useSelector`
  // once the router has been updated
  const { user } = useAppData();
  const [featureFlagUser, setFeatureFlagUser] = useState<FeatureFlagUser>(
    getUserIdentifier(user)
  );

  const workspaceResource = useResource(workspaceNavigationResource);

  const workspaceUUID = useSelector(getCurrentWorkspaceUUID);
  const projectUUID = useSelector(getCurrentProjectUuid);
  const repositoryUUID = useSelector(getCurrentRepositoryUuid);
  const repositoryProjectUUID = useSelector(getCurrentRepositoryProjectUuid);
  const repositoryWorkspaceUUID = useSelector(
    getCurrentRepositoryWorkspaceUUID
  );

  const workspaceUuid = uncurl(
    workspaceResource.data?.workspace.uuid ||
      repositoryWorkspaceUUID ||
      workspaceUUID
  );
  const projectUuid = uncurl(repositoryProjectUUID || projectUUID);
  const repositoryUuid = uncurl(repositoryUUID);

  const env = envMap[getBbEnv() || BbEnv.Development];
  const apiKey = FeatureKeys[env];

  useEffect(() => {
    setFeatureFlagUser(
      getUserIdentifier(
        user ? { ...user, workspaceUuid, projectUuid, repositoryUuid } : user
      )
    );
  }, [user, workspaceUuid, projectUuid, repositoryUuid]);

  return (
    <BaseProvider
      analyticsWebClient={analyticsClient()}
      apiKey={apiKey}
      featureFlagUser={featureFlagUser}
      options={{
        productKey: 'bitbucket',
        environment: env,
      }}
    >
      {children}
    </BaseProvider>
  );
};

// Ideally we'll import directly from the hooks package, but we have a lot of
// references to this path in the codebase. Keep backwards compatibility until
// we can refactor those references.
export { useStatsigGate } from 'src/hooks/statsig';
